import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Button,
  ListItem,
  Divider,
  IconButton,
  List,
  LinearProgress,
  ListItemText,
  Box,
} from "@mui/material";
import {
  DeleteForeverOutlined,
  MicOutlined,
  DescriptionOutlined,
  EditOutlined,
} from "@mui/icons-material";

import { useSnackbar } from "notistack";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

import { Vid } from "../../../../types/skills";
import { SkillInstance } from "../../../../src/API";

import { useProfile } from "../../../../providers/profile";
import { useSkillInstances } from "../../../../providers/state/skillInstance";
import { idFetcher } from "../../../../utilities/fetcher";

import Recorder from "../../recorder";
import ConfirmDialog from "../../confirmation";
import FileDropzone from "../../file-dropzone";
import VideoViewDialog from "../../video-view-dialog";
import TextFieldPrompt from "../../text-field-prompt";

const ResponseVideos = ({
  instance,
  refresh,
}: {
  instance: Partial<SkillInstance> | undefined;
  refresh: () => Promise<void>;
}) => {
  const [responseVideos, setResponseVideos] = useState<Vid[]>([]);
  const [viewVid, setViewVid] = useState<Vid>();
  const [viewVidOpen, setViewVidOpen] = useState(false);
  const [editVidName, setEditVidName] = useState("");
  const [editVidNameOpen, setEditVidNameOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteAction = useRef<() => void>();

  const { upsertSkillInstance } = useSkillInstances();

  const { viewProfile } = useProfile();
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!instance) return;

    setResponseVideos(JSON.parse(instance.responseVideos || "[]"));
  }, [instance]);

  const addVid = useCallback(
    async ({ urls }: { urls: [string] }) => {
      if (!instance) return;

      const add = {
        title: "",
        when: new Date().valueOf(),
        urls,
        description: "",
      };
      const result = await upsertSkillInstance({
        id: instance.id,
        responseVideos: JSON.stringify([...responseVideos, add]),
      });
      await refresh();
      return result;
    },
    [instance, refresh, responseVideos, upsertSkillInstance]
  );

  const openVid = useCallback(async (vid: any) => {
    setViewVid(vid);
    setViewVidOpen(true);
  }, []);

  const openEditVidName = useCallback((vid: any) => {
    setViewVid(vid);
    setEditVidName(vid.title);
    setEditVidNameOpen(true);
  }, []);

  const saveEditVidName = useCallback(async () => {
    if (!viewVid || !instance) return setEditVidNameOpen(false);

    const foundVid = responseVideos.find((item) => item.when === viewVid.when);
    if (foundVid) {
      foundVid.title = editVidName;

      const result = await upsertSkillInstance({
        id: instance.id,
        responseVideos: JSON.stringify(responseVideos),
      });
      await refresh();
      setEditVidNameOpen(false);
      return result;
    }
  }, [
    editVidName,
    refresh,
    instance,
    responseVideos,
    upsertSkillInstance,
    viewVid,
  ]);

  const onDocDrop = useCallback(
    async (acceptedFiles: any[]) => {
      if (!viewProfile?.account || !instance) return;

      acceptedFiles.forEach(async (file: any) => {
        try {
          const ext = file.name.split(".").pop();
          const key = encodeURIComponent(
            `private/${viewProfile.account?.sub}/${uuidv4()}.${ext}`
          );
          // console.log(key);
          const upload = await idFetcher(
            `/api/s3/put/${key}`,
            getIdTokenClaims
          );
          // console.log(upload.signedUrl);

          const uploadResult = await axios.put(upload.signedUrl, file, {
            headers: { "content-type": file.type },
            onUploadProgress: (evt) => {
              if (!evt.total) return setUploadProgress(0);
              setUploadProgress((evt.loaded / evt.total) * 100);
            },
          });
          // console.log(uploadResult);

          const add = {
            title: file.name,
            when: new Date().valueOf(),
            urls: [key],
            description: "",
          };
          const result = await upsertSkillInstance({
            id: instance.id,
            responseVideos: JSON.stringify([...responseVideos, add]),
          });
          await refresh();
          return result;
        } catch (e) {
          console.error(e);
          enqueueSnackbar("Problem with file upload. Please try again.", {
            variant: "error",
          });
        }
      });
    },
    [
      instance,
      viewProfile,
      getIdTokenClaims,
      upsertSkillInstance,
      responseVideos,
      refresh,
    ]
  );

  const deleteVid = useCallback(
    async (vid: any) => {
      if (!instance) return;

      const result = await upsertSkillInstance({
        id: instance.id,
        responseVideos: JSON.stringify(responseVideos.filter((v) => v !== vid)),
      });
      await refresh();
      return result;
    },
    [instance, refresh, responseVideos, upsertSkillInstance]
  );

  const handleDelete = useCallback(() => {
    if (deleteAction.current) deleteAction.current();
    deleteAction.current = undefined;
  }, []);

  // instance may be undefined if not assigned
  if (!instance) {
    return (
      <Card>
        <CardHeader
          title="STUDENT RESPONSE VIDEOS"
          sx={{ "& span": { fontSize: "1rem" } }}
        />
        <CardContent>
          <Alert severity="info" sx={{ fontWeight: 700 }}>
            Student video responses will populate here.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          action={
            <Button
              endIcon={<MicOutlined />}
              color="error"
              size="small"
              variant="contained"
              onClick={() => setRecorderOpen(true)}
            >
              Record
            </Button>
          }
          title="STUDENT RESPONSE VIDEOS"
          sx={{ "& span": { fontSize: "1rem" } }}
        />
        <CardContent>
          <List
            sx={{
              maxWidth: "34rem",
              mx: "auto",
            }}
            dense={true}
          >
            {responseVideos.map((vid) => (
              <Fragment key={vid.urls ? vid.urls[0] : vid.url}>
                <Divider />
                <ListItem sx={{ alignItems: "center" }}>
                  <ListItemText
                    sx={{ "& span": { fontWeight: 700 } }}
                    primary={vid.title || "Untitled"}
                    secondary={`${new Date(
                      vid.when
                    ).toLocaleDateString()} ${new Date(
                      vid.when
                    ).toLocaleTimeString()}`}
                  />
                  <Box sx={{ float: "right", display: "flex" }}>
                    <Button
                      onClick={() => {
                        openVid(vid);
                      }}
                      color="secondary"
                      endIcon={<DescriptionOutlined />}
                      size="small"
                      variant="outlined"
                    >
                      Open
                    </Button>
                    <IconButton
                      onClick={() => openEditVidName(vid)}
                      aria-label="edit"
                      size="small"
                      sx={{ marginLeft: "0.5rem" }}
                    >
                      <EditOutlined />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        deleteAction.current = () => deleteVid(vid);
                        setDeleteOpen(true);
                      }}
                      aria-label="delete"
                      size="small"
                    >
                      <DeleteForeverOutlined />
                    </IconButton>
                  </Box>
                </ListItem>
              </Fragment>
            ))}
          </List>
          <FileDropzone onDrop={onDocDrop} />
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ height: "8px", marginTop: "1rem" }}
          />
        </CardContent>
      </Card>
      <Recorder
        open={recorderOpen}
        setOpen={setRecorderOpen}
        onSave={addVid}
        isPrivate={true}
      />
      <VideoViewDialog
        open={viewVidOpen}
        setOpen={setViewVidOpen}
        vid={viewVid}
      />
      <TextFieldPrompt
        open={editVidNameOpen}
        setOpen={setEditVidNameOpen}
        value={editVidName}
        setValue={setEditVidName}
        onSave={saveEditVidName}
      />
      <ConfirmDialog
        title="Delete Response Video?"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDelete}
      >
        Are you sure you want to delete?
      </ConfirmDialog>
    </>
  );
};

export default ResponseVideos;
